import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Bars3Icon,
  XMarkIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'zh-CN', name: '简体中文' },
    { code: 'zh-TW', name: '繁體中文' },
    { code: 'ja', name: '日本語' },
  ];

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setShowLanguageMenu(false);
  };

  return (
    <nav className="bg-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <span className="text-2xl font-bold text-primary">9527.fi</span>
            </Link>
            <div className="hidden sm:ml-10 sm:flex sm:items-center space-x-8">
              <Link to="/about" className="text-gray-700 hover:text-primary px-2 py-1 text-sm font-medium">
                {t('nav.about')}
              </Link>
              <Link to="/services" className="text-gray-700 hover:text-primary px-2 py-1 text-sm font-medium">
                {t('nav.services')}
              </Link>
              <Link to="/technology" className="text-gray-700 hover:text-primary px-2 py-1 text-sm font-medium">
                {t('nav.technology')}
              </Link>
              <Link to="/performance" className="text-gray-700 hover:text-primary px-2 py-1 text-sm font-medium">
                {t('nav.performance')}
              </Link>
              <Link to="/compliance" className="text-gray-700 hover:text-primary px-2 py-1 text-sm font-medium">
                {t('nav.compliance')}
              </Link>
              <Link to="/news" className="text-gray-700 hover:text-primary px-2 py-1 text-sm font-medium">
                {t('nav.news')}
              </Link>
              <Link to="/contact" className="text-gray-700 hover:text-primary px-2 py-1 text-sm font-medium">
                {t('nav.contact')}
              </Link>
            </div>
          </div>
          <div className="hidden sm:ml-6 sm:flex sm:items-center">
            <div className="relative">
              <button
                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                className="flex items-center text-gray-700 hover:text-primary text-sm font-medium"
              >
                <GlobeAltIcon className="h-5 w-5 mr-1" />
                {languages.find(lang => lang.code === i18n.language)?.name || 'English'}
              </button>
              {showLanguageMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
                >
                  <div className="py-1">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
          <div className="-mr-2 flex items-center sm:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
            >
              <span className="sr-only">Open main menu</span>
              {isOpen ? (
                <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <motion.div
        initial={false}
        animate={isOpen ? "open" : "closed"}
        variants={{
          open: { opacity: 1, height: "auto" },
          closed: { opacity: 0, height: 0 }
        }}
        className="sm:hidden"
      >
        <div className="pt-2 pb-3 space-y-1">
          <Link to="/about" className="block px-4 py-2 text-gray-700 hover:text-primary text-base font-medium">
            {t('nav.about')}
          </Link>
          <Link to="/services" className="block px-4 py-2 text-gray-700 hover:text-primary text-base font-medium">
            {t('nav.services')}
          </Link>
          <Link to="/technology" className="block px-4 py-2 text-gray-700 hover:text-primary text-base font-medium">
            {t('nav.technology')}
          </Link>
          <Link to="/performance" className="block px-4 py-2 text-gray-700 hover:text-primary text-base font-medium">
            {t('nav.performance')}
          </Link>
          <Link to="/compliance" className="block px-4 py-2 text-gray-700 hover:text-primary text-base font-medium">
            {t('nav.compliance')}
          </Link>
          <Link to="/news" className="block px-4 py-2 text-gray-700 hover:text-primary text-base font-medium">
            {t('nav.news')}
          </Link>
          <Link to="/contact" className="block px-4 py-2 text-gray-700 hover:text-primary text-base font-medium">
            {t('nav.contact')}
          </Link>
          <div className="px-4 py-2">
            <div className="space-y-1">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  {lang.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </nav>
  );
};

export default Navbar; 